import * as React from 'react';
import { DirectionProvider as RadixDirectionProvider } from '@radix-ui/react-direction';
import { getDirection, resolveLocale, type LocaleDirection, type SupportedLocale } from '@repo/i18n';

type DirectionContextValue = {
  locale: SupportedLocale;
  dir: LocaleDirection;
};

const DirectionContext = React.createContext<DirectionContextValue | null>(null);

type AppDirectionProviderProps = {
  children: React.ReactNode;
  locale: SupportedLocale | string | null | undefined;
  syncDocument?: boolean;
};

export function AppDirectionProvider({
  children,
  locale,
  syncDocument = true,
}: AppDirectionProviderProps) {
  const resolvedLocale = React.useMemo(() => resolveLocale(locale), [locale]);
  const dir = React.useMemo(() => getDirection(resolvedLocale), [resolvedLocale]);

  React.useEffect(() => {
    if (!syncDocument || typeof document === 'undefined') {
      return;
    }
    document.documentElement.dir = dir;
  }, [dir, syncDocument]);

  const value = React.useMemo(
    () => ({ locale: resolvedLocale, dir }),
    [dir, resolvedLocale],
  );

  return (
    <DirectionContext.Provider value={value}>
      <RadixDirectionProvider dir={dir}>{children}</RadixDirectionProvider>
    </DirectionContext.Provider>
  );
}

export function useAppDirection() {
  const context = React.useContext(DirectionContext);
  if (!context) {
    throw new Error('useAppDirection must be used within AppDirectionProvider');
  }
  return context;
}
